import { ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { VendorService } from 'src/app/services/marketplace/vendor.service';
import { FollowService } from 'src/app/services/marketplace/follow.service';
import { ToastService } from 'src/app/services/toast.service';
import { Follow, Vendor } from 'src/app/services/marketplace/marketplace.models';

@Component({
  selector: 'app-all-shops',
  templateUrl: './all-shops.component.html',
  styleUrls: ['./all-shops.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AllShopsComponent implements OnInit {
  vendors: Vendor[] = [];
  loading = true;
  loadFailed = false;
  searchTerm = '';
  readonly skeletons = [1, 2, 3, 4];

  /** vendor id -> follow record id */
  followsByVendorId = new Map<number, number>();
  pendingFollowId: number | null = null;

  constructor(
    private vendorService: VendorService,
    private followService: FollowService,
    private toaster: ToastService,
    private cdr: ChangeDetectorRef,
  ) {}

  /** follower_count as loaded, adjusted for follows made on this page. */
  private followerDelta = new Map<number, number>();

  ngOnInit(): void {
    this.load();

    // Optional on this public page — signed-out visitors just see "Follow".
    this.followService
      .list()
      .pipe(catchError(() => of([] as Follow[])))
      .subscribe((follows) => {
        this.followsByVendorId = new Map(follows.map((f) => [f.vendor, f.id]));
        this.cdr.detectChanges();
      });
  }

  load(): void {
    this.loading = true;
    this.loadFailed = false;
    this.vendorService.list().subscribe({
      next: (vendors) => {
        this.vendors = vendors;
        vendors.forEach((v) => { if (v.follow_id) this.followsByVendorId.set(v.id, v.follow_id); });
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.loadFailed = true;
        this.cdr.detectChanges();
      },
    });
  }

  followerCount(vendor: Vendor): number {
    return (vendor.follower_count ?? 0) + (this.followerDelta.get(vendor.id) ?? 0);
  }

  private bumpFollowers(vendor: Vendor, by: number): void {
    this.followerDelta.set(vendor.id, (this.followerDelta.get(vendor.id) ?? 0) + by);
  }

  get filteredVendors(): Vendor[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.vendors;
    }
    return this.vendors.filter((vendor) =>
      vendor.shop_name.toLowerCase().includes(term) || (vendor.bio || '').toLowerCase().includes(term));
  }

  isFollowing(vendor: Vendor): boolean {
    return this.followsByVendorId.has(vendor.id);
  }

  /** The card is a link to the shop, so keep the toggle from navigating. */
  toggleFollow(vendor: Vendor, event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    if (this.pendingFollowId === vendor.id) {
      return;
    }
    this.pendingFollowId = vendor.id;

    const existingFollowId = this.followsByVendorId.get(vendor.id);
    if (existingFollowId != null) {
      this.followsByVendorId.delete(vendor.id); // optimistic
      this.bumpFollowers(vendor, -1);
      this.followService.unfollow(existingFollowId).subscribe({
        next: () => {
          this.pendingFollowId = null;
          this.cdr.detectChanges();
        },
        error: () => {
          this.followsByVendorId.set(vendor.id, existingFollowId); // roll back
          this.bumpFollowers(vendor, 1);
          this.pendingFollowId = null;
          this.toaster.showNote('Could not unfollow this shop.', 'danger');
          this.cdr.detectChanges();
        },
      });
      return;
    }

    // Mark followed up front so the button responds immediately, then swap in
    // the real record id. The placeholder is negative so it can never collide
    // with a server id, and preview fixtures (which return no id at all) still
    // leave the button in the right state.
    const optimisticId = -Date.now();
    this.followsByVendorId.set(vendor.id, optimisticId);
    this.bumpFollowers(vendor, 1);

    this.followService.follow(vendor.id).subscribe({
      next: (follow) => {
        if (follow?.id) {
          this.followsByVendorId.set(vendor.id, follow.id);
        }
        this.pendingFollowId = null;
        this.cdr.detectChanges();
      },
      error: () => {
        this.followsByVendorId.delete(vendor.id); // roll back
        this.bumpFollowers(vendor, -1);
        this.pendingFollowId = null;
        this.toaster.showNote('Sign in to follow shops.', 'warning');
        this.cdr.detectChanges();
      },
    });
  }
}
